import React, { useEffect, useState } from 'react';

import { MediaProvider } from '@atlaskit/media-client-react/media-provider';
import { BrowserLoader as Browser } from '@atlaskit/media-picker/browser';
import { DropzoneLoader as Dropzone } from '@atlaskit/media-picker/dropzone';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box } from '@atlaskit/primitives/compiled/box';

import type { AttachmentPickerProps } from './types';

export const AttachmentPicker = ({
	dropzoneContainer,
	onUploadEnd,
	onUploadStart,
	onUploadError,
	mediaClientConfig,
	mediaBrowserConfig,
	mediaDropzoneConfig,
	onDragEnter,
	onDragLeave,
	children: renderBrowserControl,
}: AttachmentPickerProps): React.JSX.Element => {
	const [isBrowserOpen, setIsBrowserOpen] = useState<boolean>(false);

	const browserConfig = {
		multiple: true,
		uploadParams: {},
		...(mediaBrowserConfig ?? {}),
	};

	const dropzoneConfig = {
		uploadParams: {},
		...(mediaDropzoneConfig ?? {}),
		container: dropzoneContainer,
	};

	// this is incremented whenever the mediaClientConfig and onUploadStart changes which in turn changes the key of the Browser component to trigger a remount
	// this is because the Browser component does not update its mediaClient on re-render, only remounts
	// TODO: REQIN-1937 try to fix the underlying issue in authProvider to remove this useEffect
	const [mediaConfigRefreshCount, setMediaConfigRefreshCount] = useState(0);
	useEffect(() => {
		setMediaConfigRefreshCount(mediaConfigRefreshCount + 1);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [mediaClientConfig]);

	return (
		<MediaProvider mediaClientConfig={mediaClientConfig}>
			{/*
				Putting Browser and Dropzone components inside a <div translate="no"> component is a workaround to prevent Google page translation from breaking the whole form UI in the Portal Request Create page. https://github.com/facebook/react/issues/11538.

				This is how it breaks:
				1. Google page translation mutates the text nodes in the DOM (The text nodes are rendered in the `children: renderBrowserControl` prop.), by replacing the text nodes with new <font>...</font> nodes.
				2. Then when the <Browser> component re-renders because of the `key` prop change. The re-render triggers React reconciliation in its parent node. The parent node tries to remove/insert children nodes, which include the text nodes.
				3. Now React still refers to the old DOM text nodes, not the one that were introduced by the Google page translation. So React spits out errors when trying to remove/insert the text nodes that are no longer in the DOM.

				The workaround is to wrap the re-rendering component, <Browser>, inside a parent node that doesn't have any text nodes.
			*/}

			<Box
				// eslint-disable-next-line @typescript-eslint/ban-ts-comment
				// @ts-ignore: to unblock React 18.2.0 -> 18.3.1 version bump in Jira
				translate="no"
			>
				<Browser
					key={String(mediaConfigRefreshCount)}
					isOpen={isBrowserOpen}
					config={browserConfig}
					mediaClientConfig={mediaClientConfig}
					onUploadsStart={onUploadStart}
					onError={onUploadError}
					onClose={() => setIsBrowserOpen(false)}
					onEnd={onUploadEnd}
				/>
				<Dropzone
					mediaClientConfig={mediaClientConfig}
					config={dropzoneConfig}
					onUploadsStart={onUploadStart}
					// Without onError, a failed drag-and-drop upload is never removed and the field stays
					// in the UPLOADING state, blocking form submission. Browser already handles this.
					onError={fg('proforma-jsdcloud-19007-media-token-sync') ? onUploadError : undefined}
					onDragEnter={onDragEnter}
					onDragLeave={onDragLeave}
					onEnd={onUploadEnd}
				/>
			</Box>
			{renderBrowserControl({ setIsBrowserOpen })}
		</MediaProvider>
	);
};
